import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useUiText } from '../lib/ui-text';
import { Button } from './button';
import { Input } from './input';

/** One catalog PI as returned by `/access/teams/{id}/investigator-search`. */
export interface CatalogInvestigator {
  slug: string;
  fullName: string;
  title?: string | null;
  clinicNames?: string[];
  studyCount?: number;
  email?: string | null;
  emailDomainAllowed?: boolean;
  status?: 'available' | 'member' | 'pending' | 'catalog_only' | 'other_team';
  registeredUserId?: string | null;
}

export type InvestigatorSearch = (query: string) => Promise<CatalogInvestigator[]>;

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

/** Debounced catalog lookup. Emails never hit the catalog: it is searched by name. */
export function useCatalogSearch(search: InvestigatorSearch | undefined, query: string) {
  const [state, setState] = useState<{
    query: string;
    results: CatalogInvestigator[];
    loading: boolean;
    error: boolean;
  }>({ query: '', results: [], loading: false, error: false });
  const trimmed = query.trim();
  const active = !!search && trimmed.length >= MIN_QUERY && !trimmed.includes('@');
  useEffect(() => {
    if (!active || !search) {
      setState({ query: trimmed, results: [], loading: false, error: false });
      return;
    }
    let live = true;
    setState(current => ({ ...current, loading: true, error: false }));
    const timer = setTimeout(() => {
      search(trimmed)
        .then(results => {
          if (live) setState({ query: trimmed, results, loading: false, error: false });
        })
        .catch(() => {
          if (live) setState({ query: trimmed, results: [], loading: false, error: true });
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [active, search, trimmed]);
  return { ...state, active };
}

export function investigatorPlace(pi: CatalogInvestigator): string {
  const names = pi.clinicNames ?? [];
  return [pi.title, names.slice(0, 2).join(', ')].filter(Boolean).join(' · ');
}

/**
 * Pick one catalog PI by name. Used inside the create-member form and on a
 * member row, so the input is a plain textbox and never competes with the
 * member search box for the searchbox role.
 */
export function InvestigatorPicker({
  id,
  search,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  search: InvestigatorSearch;
  value: CatalogInvestigator | null;
  onChange: (pi: CatalogInvestigator | null) => void;
  disabled?: boolean;
}) {
  const uiText = useUiText();
  const [query, setQuery] = useState('');
  const lookup = useCatalogSearch(search, query);

  if (value) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-3xl bg-muted/50 p-3 text-sm">
        <div className="min-w-0">
          <p className="font-medium">{value.fullName}</p>
          {investigatorPlace(value) && (
            <p className="text-muted-foreground">{investigatorPlace(value)}</p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => {
            onChange(null);
            setQuery('');
          }}
        >
          {uiText('changeSelection')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground"
        />
        <Input
          id={id}
          type="text"
          autoComplete="off"
          className="pl-9"
          aria-label={uiText('findInCatalog')}
          placeholder={uiText('findInCatalog')}
          disabled={disabled}
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>
      {lookup.active && lookup.loading && (
        <p role="status" className="text-xs text-muted-foreground">
          {uiText('searchingCatalog')}
        </p>
      )}
      {lookup.active && lookup.error && (
        <p role="alert" className="text-xs">
          {uiText('catalogSearchFailed')}
        </p>
      )}
      {lookup.active && !lookup.loading && !lookup.error && lookup.results.length === 0 && (
        <p role="status" className="text-xs text-muted-foreground">
          {uiText('noCatalogMatches')}
        </p>
      )}
      {lookup.results.length > 0 && (
        <ul className="space-y-1" aria-label={uiText('findInCatalog')}>
          {lookup.results.map(pi => (
            <li key={pi.slug}>
              <button
                type="button"
                disabled={disabled}
                className="flex w-full flex-col items-start rounded-3xl px-3 py-2 text-left text-sm hover:bg-muted"
                aria-label={`${uiText('select')} ${pi.fullName}`}
                onClick={() => {
                  onChange(pi);
                  setQuery('');
                }}
              >
                <span className="font-medium">{pi.fullName}</span>
                {investigatorPlace(pi) && (
                  <span className="text-xs text-muted-foreground">{investigatorPlace(pi)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
